import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { BlockVisualization, Size } from 'webapp/enums/enums';

import BarLoaderComponent from 'webapp/components/bar-loader';
import BlocktypePickerComponent from 'webapp/components/blocktype-picker';
import CommonAskToConnectButtonComponent from 'webapp/components/common/ask-to-connect-button';
import CommonGenieInputComponent from 'webapp/components/common/genie/input';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectComponent from 'webapp/components/form/select';
import StorybookDesignTokensComponent from 'webapp/components/storybook/design-tokens';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type SourceType from 'webapp/models-custom/source-type';

export default class StorybookIndexRouteTemplate extends Component {
	linksModifiers = ['green', 'blue', 'red', 'secondary', 'underline'];
	askToConnectButtonComponent = {
		model: {} as SourceType,
	};

	buttonThemes = [
		'green',
		'green-outlined',
		'white',
		'white-outlined-red',
		'white-outlined-green',
		'white-outlined-blue',
		'grey',
		'grey-outlined',
		'grey-empty',
		'blue',
		'blue-outlined',
		'blue-darker',
		'red',
		'red-outlined',
		'orange',
		'purple-gradient',
	];
	buttonSizes = [Size.TINY, Size.SMALL, Size.NORMAL, Size.BIG];

	barLoaderThemes: ('grey' | 'white')[] = ['grey', 'white'];
	barLoaderSizes = [Size.TINY, Size.SMALL, Size.NORMAL, Size.BIG];

	selectThemes = ['default', 'blue', 'grey'];
	selectSizes = [Size.TINY, Size.SMALL, Size.NORMAL];
	selectOptions = [
		{ value: 1, label: 'Daily' },
		{ value: 2, label: 'Weekly' },
		{ value: 3, label: 'Monthly' },
	];
	selectOptionSelected = 1;

	tooltipThemes = ['default', 'light', 'red', 'orange'];

	checkboxSwitchThemes = ['default', 'green', 'navy'];
	checkboxSwitchSizes = [Size.SMALL, Size.NORMAL];

	@tracked checkboxSwitchSelectedOption = true;

	@action
	selectCheckboxSwitchOption(value: boolean) {
		this.checkboxSwitchSelectedOption = value;
	}

	blocktypePickerThemes = ['light', 'dark'];

	@tracked blockypeValue = BlockVisualization.BAR_CHART;

	@action
	changeBlocktypeValue(value: BlockVisualization) {
		this.blockypeValue = value;
	}

	private iconCircle2: IconName = 'NumberCircle2Fill';
	private iconCircle3: IconName = 'NumberCircle3Fill';

	radiobuttonSwitcherThemes = ['default', 'default-2', 'light', 'dark', 'dark-blue', 'alternate'];
	radiobuttonSwitcherSizes = ['default', 'small'];
	radiobuttonSwitcherOptions = [
		{ value: 'one', label: 'One', disabled: false },
		{ value: 'two', label: 'Two', disabled: true, icon: this.iconCircle2 },
		{ value: 'three', label: '', disabled: false, icon: this.iconCircle3 },
	];

	@tracked radiobuttonSwitcherSelectedOption = 'one';

	@action
	changeRadiobuttonSwitcherOption(option: AnySimpleValue) {
		this.radiobuttonSwitcherSelectedOption = option as string;
	}

	@action
	dummyAction() {
		// do nothing
	}

	<template>
		<nav>
			<LinkTo @route="storybook.bubbles" class="link link--blue">Blue Bubble Tours</LinkTo>
		</nav>

		<h1>Links</h1>

		<h2>Class link modifiers</h2>

		<div class="grid">
			{{#each this.linksModifiers as |linkModifier|}}
				<div class="item">
					<div class="item__title">
						link link--{{linkModifier}}
					</div>
					<div class="item__content">
						<a role="button" class="link link--{{linkModifier}}">Modified link</a>
					</div>
				</div>
			{{/each}}
		</div>

		<h2>Ask To Connect <em>(Common::AskToConnectButton)</em></h2>

		<div class="grid">
			<div class="item">
				<div class="item__content">
					<CommonAskToConnectButtonComponent
						@model={{this.askToConnectButtonComponent.model}}
					/>
				</div>
			</div>
		</div>

		<h1>Buttons <em>(SubmitButton)</em></h1>

		<h2>Themes</h2>

		<div class="grid">
			{{#each this.buttonThemes as |buttonTheme|}}
				<div class="item">
					<div class="item__title">
						theme-{{buttonTheme}}
					</div>
					<div class="item__content">
						<SubmitButtonComponent
							@theme={{buttonTheme}}
							@onTrigger={{this.dummyAction}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<h2>Sizes</h2>

		<div class="grid">
			{{#each this.buttonSizes as |buttonSize|}}
				<div class="item">
					<div class="item__title">
						size-{{buttonSize}}
					</div>
					<div class="item__content">
						<SubmitButtonComponent
							@icon="Refresh"
							@size={{buttonSize}}
							@onTrigger={{this.dummyAction}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<hr />

		<h1>Genie Inputs <em>(Common::Genie::Input)</em></h1>

		<div class="grid">
			<div class="item">
				<div class="item__title">
					default
				</div>
				<div class="item__content">
					<CommonGenieInputComponent
						@placeholder="Ask Genie..."
						@onSubmit={{this.dummyAction}}
					/>
				</div>
			</div>
			<div class="item">
				<div class="item__title">
					custom placeholder
				</div>
				<div class="item__content">
					<CommonGenieInputComponent
						@placeholder="You can ask follow-ups like 'What caused this?'"
						@onSubmit={{this.dummyAction}}
					/>
				</div>
			</div>
			<div class="item">
				<div class="item__title">
					no logo
				</div>
				<div class="item__content">
					<CommonGenieInputComponent @hideLogo={{true}} @onSubmit={{this.dummyAction}} />
				</div>
			</div>
		</div>

		<hr />

		<h1>Bar Loaders <em>(BarLoader)</em></h1>

		<h2>Themes</h2>

		<div class="grid">
			{{#each this.barLoaderThemes as |barLoaderTheme|}}
				<div class="item">
					<div class="item__title">
						theme-{{barLoaderTheme}}
					</div>
					<div class="item__content">
						<BarLoaderComponent @theme={{barLoaderTheme}} />
					</div>
				</div>
			{{/each}}
		</div>

		<h2>Sizes</h2>

		<div class="grid">
			{{#each this.barLoaderSizes as |barLoaderSize|}}
				<div class="item">
					<div class="item__title">
						size-{{barLoaderSize}}
					</div>
					<div class="item__content">
						<BarLoaderComponent @size={{barLoaderSize}} />
					</div>
				</div>
			{{/each}}
		</div>

		<hr />

		<h1>Select <em>(Form::Select)</em></h1>

		<h2>Themes</h2>

		<div class="grid">
			{{#each this.selectThemes as |selectTheme|}}
				<div class="item">
					<div class="item__title">
						theme-{{selectTheme}}
					</div>
					<div class="item__content">
						<FormSelectComponent
							@selected={{this.selectOptionSelected}}
							@options={{this.selectOptions}}
							@onChange={{this.dummyAction}}
							@theme={{selectTheme}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<h2>Sizes</h2>

		<div class="grid">
			{{#each this.selectSizes as |selectSize|}}
				<div class="item">
					<div class="item__title">
						size-{{selectSize}}
					</div>
					<div class="item__content">
						<FormSelectComponent
							@selected={{this.selectOptionSelected}}
							@options={{this.selectOptions}}
							@onChange={{this.dummyAction}}
							@size={{selectSize}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<hr />

		<h1>Tooltip <em>(ToolTip)</em></h1>

		<h2>Themes</h2>

		<div class="grid">
			{{#each this.tooltipThemes as |tooltipTheme|}}
				<div class="item">
					<div class="item__title">
						tooltipster-theme-{{tooltipTheme}}
					</div>
					<div class="item__content">
						<ToolTipComponent
							@text="Tooltip<br><a href=''>Tooltip link</a>"
							@theme={{tooltipTheme}}
							@interactive={{true}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<hr />

		<h1>CheckboxSwitch <em>(Form::CheckboxSwitch)</em></h1>

		<h2>Themes</h2>

		<div class="grid">
			{{#each this.checkboxSwitchThemes as |checkboxSwitchTheme|}}
				<div class="item">
					<div class="item__title">
						theme-{{checkboxSwitchTheme}}
					</div>
					<div class="item__content">
						<FormCheckboxSwitchComponent
							@name="checkbox-switch"
							@selected={{this.checkboxSwitchSelectedOption}}
							@theme={{checkboxSwitchTheme}}
							@onChange={{this.selectCheckboxSwitchOption}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<h2>Sizes</h2>

		<div class="grid">
			{{#each this.checkboxSwitchSizes as |checkboxSwitchSize|}}
				<div class="item">
					<div class="item__title">
						size-{{checkboxSwitchSize}}
					</div>
					<div class="item__content">
						<FormCheckboxSwitchComponent
							@name="checkbox-switch"
							@selected={{this.checkboxSwitchSelectedOption}}
							@size={{checkboxSwitchSize}}
							@onChange={{this.selectCheckboxSwitchOption}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<hr />

		<h1>Blocktype Picker <em>(BlocktypePicker)</em></h1>

		<h2>Themes</h2>

		<div class="grid">
			{{#each this.blocktypePickerThemes as |blocktypePickerTheme|}}
				<div class="item">
					<div class="item__title">
						theme-{{blocktypePickerTheme}}
					</div>
					<div class="item__content">
						<BlocktypePickerComponent
							@blockType={{this.blockypeValue}}
							@isAttributed={{false}}
							@theme={{blocktypePickerTheme}}
							@onChangeBlockType={{this.changeBlocktypeValue}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<hr />

		<h1>Radiobutton Switcher <em>(Form::RadiobuttonGroup)</em></h1>

		<h2>Themes</h2>

		<div class="grid">
			{{#each this.radiobuttonSwitcherThemes as |radiobuttonSwitcherTheme|}}
				<div class="item">
					<div class="item__title">
						theme-{{radiobuttonSwitcherTheme}}
					</div>
					<div class="item__content">
						<FormRadiobuttonGroupComponent
							class="radiobutton-switcher"
							@name="radiobutton_switcher"
							@selected={{this.radiobuttonSwitcherSelectedOption}}
							@options={{this.radiobuttonSwitcherOptions}}
							@onChange={{this.changeRadiobuttonSwitcherOption}}
							@theme={{radiobuttonSwitcherTheme}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<h2>Sizes</h2>

		<div class="grid">
			{{#each this.radiobuttonSwitcherSizes as |radiobuttonSwitcherSize|}}
				<div class="item">
					<div class="item__title">
						size-{{radiobuttonSwitcherSize}}
					</div>
					<div class="item__content">
						<FormRadiobuttonGroupComponent
							class="radiobutton-switcher"
							@name="radiobutton_switcher"
							@selected={{this.radiobuttonSwitcherSelectedOption}}
							@options={{this.radiobuttonSwitcherOptions}}
							@onChange={{this.changeRadiobuttonSwitcherOption}}
							@theme="dark"
							@size={{radiobuttonSwitcherSize}}
						/>
					</div>
				</div>
			{{/each}}
		</div>

		<hr />

		<StorybookDesignTokensComponent />
	</template>
}
